import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import {
    Button,
    Card,
    Chip,
    FormControl,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useAuth } from "../../hooks/useAuth";
import * as NotificationService from "../../services/notifications";
import ConfirmationModal from "../../components/ui/ConfirmationModal";
import { Container, CardContainer, CampaignCard } from "./styles";

const STATUS_MAP = {
    APPROVED: { text: "Aprovado", color: "success" },
    PENDING: { text: "Pendente", color: "warning" },
    DENIED: { text: "Negado", color: "error" },
};

const CAMPAIGN_LABELS = {
    ONE_TIME: "Campanha Única",
    CAMPAIGN_WINDOW: "Janela de Campanha",
};

const EMPTY_FORM = {
    id: null,
    title: "",
    text: "",
    campaignType: "ONE_TIME",
    scheduledDate: "",
    startDate: "",
    endDate: "",
};

const Notifications = () => {
    const { loggedMechanic } = useAuth();
    const [campaigns, setCampaigns] = useState([]);
    const [form, setForm] = useState({ ...EMPTY_FORM });
    const [editing, setEditing] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    useEffect(() => {
        fetchCampaigns();
    }, [loggedMechanic.id]);

    async function fetchCampaigns() {
        try {
            const data = await NotificationService.getNotificationsByMechanicId(loggedMechanic.id);
            setCampaigns(Array.isArray(data) ? data : [data].filter(Boolean));
        } catch {
            setCampaigns([]);
        }
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        let limited = value;
        if (name === "title") limited = value.slice(0, 60);
        if (name === "text") limited = value.slice(0, 200);
        setForm((prev) => ({ ...prev, [name]: limited }));
    };

    const handleTypeChange = (e) => {
        setForm((prev) => ({
            ...prev,
            campaignType: e.target.value,
            scheduledDate: "",
            startDate: "",
            endDate: "",
        }));
    };

    function toISOWithTime(dateStr, hours = 11, minutes = 0) {
        if (!dateStr) return null;
        const d = new Date(`${dateStr}T${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`);
        return d.toISOString();
    }

    const handleSubmit = async (e) => {
        e.preventDefault();

        const body = {
            ...(form.id ? { id: form.id } : {}),
            title: form.title,
            text: form.text,
            mechanicId: loggedMechanic.id,
            campaignType: form.campaignType,
        };

        if (form.campaignType === "ONE_TIME") {
            body.scheduledDate = toISOWithTime(form.scheduledDate);
        } else {
            body.startDate = toISOWithTime(form.startDate);
            body.endDate = toISOWithTime(form.endDate, 23, 59);
        }

        try {
            await NotificationService.sendNotification(body);
            alert(form.id ? "Campanha atualizada e reenviada para aprovação!" : "Campanha enviada para aprovação!");
            resetForm();
            fetchCampaigns();
        } catch {
            alert("Erro ao enviar a campanha.");
        }
    };

    const handleEdit = (campaign) => {
        setForm({
            id: campaign.id,
            title: campaign.title || "",
            text: campaign.text || "",
            campaignType: campaign.campaignType || "ONE_TIME",
            scheduledDate: campaign.scheduledDate ? toDateOnly(campaign.scheduledDate) : "",
            startDate: campaign.startDate ? toDateOnly(campaign.startDate) : "",
            endDate: campaign.endDate ? toDateOnly(campaign.endDate) : "",
        });
        setEditing(true);
        window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await NotificationService.deleteNotification(deleteTarget);
            alert("Campanha removida!");
            setDeleteTarget(null);
            fetchCampaigns();
        } catch {
            alert("Erro ao remover a campanha.");
        }
    };

    const resetForm = () => {
        setForm({ ...EMPTY_FORM });
        setEditing(false);
    };

    function toDateOnly(isoStr) {
        if (!isoStr) return "";
        const d = new Date(isoStr);
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    }

    function formatDate(isoStr) {
        if (!isoStr) return "—";
        return new Date(isoStr).toLocaleDateString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    }

    function getSentFlags(c) {
        const flags = [];
        if (c.campaignType === "ONE_TIME") {
            flags.push({ label: "Enviado", done: c.oneTimeSent });
        } else {
            flags.push({ label: "Início enviado", done: c.startNotificationSent });
            flags.push({ label: "Lembrete enviado", done: c.reminderNotificationSent });
        }
        return flags;
    }

    const statusColor = (status) => STATUS_MAP[status]?.color || "default";
    const statusText = (status) => STATUS_MAP[status]?.text || status;
    const borderColor = (status) =>
        status === "APPROVED" ? "#4caf50" : status === "DENIED" ? "#f44336" : "#2196f3";

    return (
        <Container>
            <Box
                mb={3}
                fontWeight="bold"
                color="#666666"
                fontSize={{ xs: "h7.fontSize", sm: "h6.fontSize", md: "h5.fontSize" }}
            >
                Notificações
            </Box>

            {/* Campaigns list */}
            <Box mb={3}>
                <Typography variant="h6" color="#333" gutterBottom>
                    Suas campanhas
                </Typography>
                {campaigns.length === 0 && (
                    <Typography color="#999">Nenhuma campanha cadastrada.</Typography>
                )}
                {campaigns.map((c) => (
                    <CampaignCard key={c.id} $borderColor={borderColor(c.status)}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box flex={1}>
                                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                    <Typography fontWeight="bold" fontSize={16}>
                                        {c.title}
                                    </Typography>
                                    <Chip
                                        label={statusText(c.status)}
                                        color={statusColor(c.status)}
                                        size="small"
                                    />
                                    <Chip
                                        label={CAMPAIGN_LABELS[c.campaignType] || c.campaignType}
                                        size="small"
                                        variant="outlined"
                                    />
                                </Box>
                                <Typography variant="body2" color="#555" mb={1}>
                                    {c.text}
                                </Typography>

                                {c.campaignType === "ONE_TIME" && (
                                    <Typography variant="body2" color="#777">
                                        {c.scheduledDate
                                            ? `Agendado para: ${formatDate(c.scheduledDate)}`
                                            : "Envio imediato após aprovação"}
                                    </Typography>
                                )}
                                {c.campaignType === "CAMPAIGN_WINDOW" && (
                                    <Typography variant="body2" color="#777">
                                        Período: {formatDate(c.startDate)} → {formatDate(c.endDate)}
                                    </Typography>
                                )}

                                <Box display="flex" gap={1} mt={1}>
                                    {getSentFlags(c).map((f) => (
                                        <Chip
                                            key={f.label}
                                            label={f.label}
                                            size="small"
                                            color={f.done ? "success" : "default"}
                                            variant={f.done ? "filled" : "outlined"}
                                        />
                                    ))}
                                </Box>

                                {c.status === "DENIED" && c.reason && (
                                    <Typography variant="body2" color="error" mt={1}>
                                        Motivo: {c.reason}
                                    </Typography>
                                )}
                            </Box>
                            <Box display="flex" gap={0.5}>
                                <Tooltip title="Editar">
                                    <IconButton size="small" onClick={() => handleEdit(c)}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Remover">
                                    <IconButton size="small" color="error" onClick={() => setDeleteTarget(c.id)}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    </CampaignCard>
                ))}
            </Box>

            {/* Campaign form */}
            <CardContainer>
                <Typography variant="h6" color="#111">
                    {editing ? "Editar campanha" : "Nova campanha"}
                </Typography>
                <Typography variant="body2" color="#666">
                    Preencha o formulário abaixo. A campanha será enviada para aprovação do time OiCar.
                    Após aprovada, as notificações serão enviadas automaticamente aos seus clientes.
                </Typography>

                <form onSubmit={handleSubmit}>
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        <FormControl>
                            <Typography variant="subtitle2" color="#555" mb={0.5}>
                                Tipo de campanha
                            </Typography>
                            <RadioGroup
                                row
                                name="campaignType"
                                value={form.campaignType}
                                onChange={handleTypeChange}
                            >
                                <FormControlLabel
                                    value="ONE_TIME"
                                    control={<Radio />}
                                    label="Campanha Única"
                                />
                                <FormControlLabel
                                    value="CAMPAIGN_WINDOW"
                                    control={<Radio />}
                                    label="Janela de Campanha"
                                />
                            </RadioGroup>
                        </FormControl>

                        {form.campaignType === "ONE_TIME" && (
                            <Card variant="outlined" sx={{ p: 2, bgcolor: "#fafafa" }}>
                                <Typography variant="body2" color="#777" mb={1}>
                                    O envio será feito uma única vez. Deixe a data em branco para enviar imediatamente após a aprovação,
                                    ou agende para uma data futura.
                                </Typography>
                                <TextField
                                    type="date"
                                    name="scheduledDate"
                                    label="Data de envio"
                                    value={form.scheduledDate}
                                    onChange={handleChange}
                                    InputLabelProps={{ shrink: true }}
                                    required
                                    fullWidth
                                    size="small"
                                />
                                <Typography variant="caption" color="#999" mt={1}>
                                    *A notificação será enviada às 11:00h (horário de Brasília) na data selecionada.
                                </Typography>
                            </Card>
                        )}

                        {form.campaignType === "CAMPAIGN_WINDOW" && (
                            <Card variant="outlined" sx={{ p: 2, bgcolor: "#fafafa" }}>
                                <Typography variant="body2" color="#777" mb={1}>
                                    Defina o período da promoção. O OiCar enviará automaticamente uma notificação no início
                                    e um lembrete no meio do período.
                                </Typography>
                                <Box display="flex" gap={2} flexWrap="wrap">
                                    <TextField
                                        type="date"
                                        name="startDate"
                                        label="Data de início"
                                        value={form.startDate}
                                        onChange={handleChange}
                                        InputLabelProps={{ shrink: true }}
                                        required
                                        size="small"
                                        sx={{ flex: 1, minWidth: 220 }}
                                    />
                                    <TextField
                                        type="date"
                                        name="endDate"
                                        label="Data de término"
                                        value={form.endDate}
                                        onChange={handleChange}
                                        InputLabelProps={{ shrink: true }}
                                        required
                                        size="small"
                                        sx={{ flex: 1, minWidth: 220 }}
                                    />
                                </Box>
                                <Typography variant="caption" color="#999" mt={1}>
                                    *As notificações serão enviadas às 11:00h (horário de Brasília) nas datas correspondentes.
                                </Typography>
                            </Card>
                        )}

                        <TextField
                            value={form.title}
                            required
                            name="title"
                            label="Título"
                            helperText={`${form.title.length}/60 caracteres`}
                            variant="outlined"
                            fullWidth
                            onChange={handleChange}
                        />
                        <TextField
                            value={form.text}
                            required
                            name="text"
                            label="Conteúdo"
                            helperText={`${form.text.length}/200 caracteres`}
                            variant="outlined"
                            fullWidth
                            onChange={handleChange}
                        />

                        <Typography variant="subtitle2" color="#666">
                            Pré-visualização:
                        </Typography>
                        <Card sx={{ bgcolor: "#e9e9e9", p: 1.5, maxWidth: 650 }}>
                            <Typography variant="body2" fontWeight="bold" color="#555">
                                {loggedMechanic.name}: {form.title || "Título"}
                            </Typography>
                            <Typography variant="body2" color="#666">
                                {form.text || "Conteúdo da notificação"}
                            </Typography>
                        </Card>

                        <Box display="flex" gap={2} mt={1}>
                            <Button
                                variant="contained"
                                color="primary"
                                type="submit"
                                sx={{ height: 48, fontWeight: "bold", boxShadow: "none" }}
                            >
                                {editing ? "Atualizar e Reenviar para Aprovação" : "Enviar para Aprovação"}
                            </Button>
                            {editing && (
                                <Button variant="outlined" onClick={resetForm} sx={{ height: 48 }}>
                                    Cancelar Edição
                                </Button>
                            )}
                        </Box>
                    </Box>
                </form>
            </CardContainer>

            <ConfirmationModal
                open={deleteTarget !== null}
                setOpen={() => setDeleteTarget(null)}
                subText="Deseja mesmo remover esta campanha? Ela será excluída permanentemente."
                handleConfirmation={handleDelete}
            />
        </Container>
    );
};

export default Notifications;
